import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArcLayer, GeoJsonLayer } from 'deck.gl'
import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps'
import { GeoJsonPoint } from '~/prix/types/geoJson'
import {
  OverlayLoading,
  OverlaySearchableMap,
} from '~/packages/legalEntityGeoprocessing/map/legalEntityGeoprocessingMap.screen'
import PulseLoader from 'react-spinners/PulseLoader'
import Modal from '~/prix/react/components/modal'
import LegalEntityReadOne from '~/packages/legalEntity/read/legalEntityReadOne.component'
import LegalEntityReadOneLite from '~/packages/legalEntity/read/legalEntityReadOneLite.component'
import { useMedia } from 'react-use'

export interface Features {
  geometry: GeoJsonPoint
  properties: {
    id: number
    name: string
    scalerank: number
  }
  type: string
}

interface Data {
  type: string
  features: Features[]
}

export interface attendantsComparison {
  id?: string
  name?: string
}

export interface MapCenterFormatted {
  type: 'Feature'
  geometry: {
    type: 'Point'
    coordinates: number[]
  }
  properties: Record<string, any>
}

export interface ArcColorConfig {
  fill: [number, number, number]
  arcSource: [number, number, number]
  arcTarget: [number, number, number]
}

interface AttendantMapProps {
  center: MapCenterFormatted | null
  zoom: number
  data: Data | null
  attendant: attendantsComparison | attendantsComparison[] | null
  isLoading: boolean
  onMapReadyChange?: (isReady: boolean) => void
  setLegalEntityName?: (value: string) => void
  selectedLegalEntityId?: string | undefined
  setSelectedLegalEntityId: (value: string | undefined) => void
  disableOverlay?: boolean
  isLiteVersion?: boolean
  arcColor?: ArcColorConfig
}

export default function AttendantMap({
  center,
  zoom,
  data,
  attendant,
  isLoading,
  onMapReadyChange,
  setLegalEntityName,
  selectedLegalEntityId,
  setSelectedLegalEntityId,
  disableOverlay,
  isLiteVersion,
  arcColor,
}: AttendantMapProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [enableBackButton, setEnableBackButton] = useState<boolean>(false)
  const [isMapLoading, setIsMapLoading] = useState(true)
  const overlayRef = useRef<DeckOverlay | null>(null)

  const mapId = useMemo(
    () =>
      `map-${
        Array.isArray(attendant)
          ? attendant.map(a => a.id).join('-')
          : attendant?.id || Math.random().toString(36).slice(2)
      }`,
    [attendant],
  )

  const firstCenter = useMemo(() => {
    const coords = Array.isArray(center)
      ? center[0]?.geometry?.coordinates
      : center?.geometry?.coordinates
    if (coords) return { lat: coords[1], lng: coords[0] }
    return { lat: -10.78, lng: -53.09 }
  }, [center])

  useEffect(() => {
    onMapReadyChange?.(false)
  }, [center, data, zoom, onMapReadyChange])

  useEffect(() => {
    if (!ref.current) return
    setIsMapLoading(true)

    const mapElement = document.getElementById(mapId)
    if (!mapElement) return

    const mapInstance = new google.maps.Map(mapElement, {
      center: firstCenter,
      mapId: '90f87356969d889c',
      tilt: 30,
      zoom,
      heading: 0,
      mapTypeId: 'hybrid',
    })

    setMap(mapInstance)
    const tilesLoadedListener = mapInstance.addListener('tilesloaded', () => {
      setIsMapLoading(false)

      if (!center) {
        return
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          onMapReadyChange?.(true)
        })
      })
    })

    const datasets = data ? [data] : []
    const centers = center ? [center] : []
    const colorSets = arcColor
      ? [arcColor]
      : [
          { fill: [0, 94, 184], arcSource: [154, 205, 254], arcTarget: [0, 94, 184] },
          { fill: [255, 165, 0], arcSource: [255, 220, 150], arcTarget: [255, 165, 0] },
        ]

    const layers = datasets.flatMap((dataset, i) => {
      const centerCoords = centers[i]?.geometry?.coordinates || centers[0]?.geometry?.coordinates
      const colors = colorSets[i % colorSets.length]

      return [
        new GeoJsonLayer({
          id: `legalEntities-${i}`,
          data: dataset,
          filled: true,
          pointRadiusMinPixels: 3,
          pointRadiusScale: 1,
          getPointRadius: (d: Features) => 11 - d.properties.scalerank,
          getFillColor: colors.fill,
          pickable: true,
          autoHighlight: true,
          onClick: (info: any) =>
            info.object && setSelectedLegalEntityId(`${info.object.properties.id}`),
        }),
        new ArcLayer({
          id: `arcs-${i}`,
          data: dataset.features.filter(f => f.properties.scalerank < 3),
          getSourcePosition: () => [centerCoords[0], centerCoords[1]],
          getTargetPosition: (d: Features) => d.geometry.coordinates,
          getSourceColor: colors.arcSource,
          getTargetColor: colors.arcTarget,
          getWidth: 2,
          pickable: true,
        }),
      ]
    })

    if (layers.length > 0) {
      const overlayInstance = new DeckOverlay({
        layers,
        glOptions: {
          preserveDrawingBuffer: true,
        },
      })
      overlayInstance.setMap(mapInstance)
      overlayRef.current = overlayInstance
    }

    return () => {
      tilesLoadedListener.remove()
      overlayRef.current?.finalize()
      overlayRef.current?.setMap(null)
      overlayRef.current = null
    }
  }, [center, zoom, data, mapId, attendant, firstCenter, setSelectedLegalEntityId, onMapReadyChange])

  useEffect(() => {
    if (map && center) {
      const coords = Array.isArray(center)
        ? center[0]?.geometry?.coordinates
        : center?.geometry?.coordinates
      if (coords) map.setCenter({ lat: coords[1], lng: coords[0] })
    }
  }, [map, center])

  const hasData = useMemo(() => {
    if (Array.isArray(data)) return data.some(d => d?.features?.length > 0)
    return data?.features && data.features.length > 0
  }, [data])

  const isMobile = useMedia('(max-width: 768px)', false)

  const modalCustomStyles = isLiteVersion
    ? {
        content: {
          maxWidth: isMobile ? '100vw' : '800px',
          minWidth: isMobile ? '100vw' : '600px',

          top: 0,
          bottom: 0,

          right: 0,
          left: 'auto',
        },
      }
    : undefined

  return (
    <div ref={ref} id={mapId} style={{ height: '100%', width: '100%' }}>
      {(isLoading || isMapLoading) && !disableOverlay && (
        <OverlayLoading>
          <div className='center'>
            <PulseLoader color={'#fff'} />
          </div>
        </OverlayLoading>
      )}

      {!hasData && !disableOverlay && (
        <OverlaySearchableMap>
          <div className='center'>
            {attendant ? (
              <span>Sem dados para essa visualização.</span>
            ) : (
              <span>Escolha um atendente para visualizar os dados.</span>
            )}
          </div>
        </OverlaySearchableMap>
      )}

      <Modal
        isOpen={!!selectedLegalEntityId}
        onClose={() => setSelectedLegalEntityId(undefined)}
        scrollY={true}
        modalStyle={isLiteVersion ? 'default' : 'fullScreen'}
        hideCloseButton={true}
        customStyles={modalCustomStyles}
      >
        {selectedLegalEntityId &&
          (isLiteVersion ? (
            <LegalEntityReadOneLite
              id={selectedLegalEntityId}
              onClose={() => setSelectedLegalEntityId(undefined)}
              enableBackButton={setEnableBackButton}
              setLegalEntityName={setLegalEntityName}
            />
          ) : (
            <LegalEntityReadOne
              id={selectedLegalEntityId}
              onClose={() => setSelectedLegalEntityId(undefined)}
              enableBackButton={setEnableBackButton}
              setLegalEntityName={setLegalEntityName}
            />
          ))}
      </Modal>
    </div>
  )
}
